// =============================================================================
// Normascan — Plateforme d'analyse normative par IA
// Copyright (c) 2026-2027 Evalens SAS — Tous droits réservés
// Marque Normascan déposée INPI n°5242986 (classes 9 et 42)
// Site : https://app.normascan.fr
// =============================================================================
import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { getAdminRatingsPaginated, getAdminRatings, getAdminSuggestions, adminDeleteRating, adminDeleteSuggestion, exportRatings, exportSuggestions, telechargerRapport, updateSuggestion } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../i18n';
import SkeletonRows from '../components/SkeletonRows';
import ConfirmModal from '../components/ConfirmModal';

const STARS = [1, 2, 3, 4, 5];
const getSuggestionCategories = (t) => ({
  extraction: t('sugg_cat_extraction'),
  display:    t('sugg_cat_display'),
  feature:    t('sugg_cat_feature'),
  bug:        t('sugg_cat_bug'),
  other:      t('sugg_cat_other'),
});
const SUGG_STATUS_COLORS = {
  open:        { bg: '#eff6ff', text: '#1d4ed8' },
  in_progress: { bg: '#fef3c7', text: '#92400e' },
  done:        { bg: '#f0fdf4', text: '#166534' },
  rejected:    { bg: '#fee2e2', text: '#991b1b' },
};

const StarDisplay = ({ score }) => (
  <span style={{ fontSize: 15, letterSpacing: 1 }}>
    {STARS.map(s => (
      <span key={s} style={{ color: s <= score ? '#f59e0b' : 'var(--gray)' }}>★</span>
    ))}
  </span>
);

const SuggestionPanel = ({ sugg, onClose, onSaved, t }) => {
  const STATUSES = ['open', 'in_progress', 'done', 'rejected'];
  const labels   = { open: t('sugg_status_open'), in_progress: t('sugg_status_in_progress'), done: t('sugg_status_done'), rejected: t('sugg_status_rejected') };
  const [status, setStatus]       = useState(sugg.status || 'open');
  const [reply, setReply]         = useState(sugg.admin_reply || '');
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');

  const save = async () => {
    setSaving(true); setError('');
    try {
      await updateSuggestion(sugg.id, { status, admin_reply: reply });
      onSaved(sugg.id, status, reply);
      onClose();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 340, background: 'var(--bg-surface)', borderLeft: '1px solid var(--gray)', boxShadow: '-4px 0 20px rgba(0,0,0,0.12)', zIndex: 200, display: 'flex', flexDirection: 'column', padding: 24, gap: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontWeight: 700, fontSize: 15, color: 'var(--black)' }}>💡 Suggestion</span>
        <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: 'var(--text-muted)' }}>✕</button>
      </div>

      <div style={{ fontSize: 12, color: 'var(--text-muted)', background: 'rgba(128,128,128,0.06)', borderRadius: 8, padding: '8px 12px' }}>
        {sugg.content?.substring(0, 120)}{sugg.content?.length > 120 ? '...' : ''}
      </div>

      <div>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Statut</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {STATUSES.map(s => {
            const c = SUGG_STATUS_COLORS[s];
            return (
              <div key={s} onClick={() => setStatus(s)} style={{ padding: '8px 12px', borderRadius: 8, border: `1.5px solid ${status === s ? 'var(--blue)' : 'var(--gray)'}`, background: status === s ? 'rgba(59,130,246,0.08)' : 'transparent', cursor: 'pointer' }}>
                <span style={{ fontSize: 11, fontWeight: 600, padding: '1px 6px', borderRadius: 99, background: c.bg, color: c.text }}>{labels[s]}</span>
              </div>
            );
          })}
        </div>
      </div>

      <div>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{t('panel_admin_reply')}</div>
        <textarea
          value={reply}
          onChange={e => setReply(e.target.value)}
          rows={4}
          style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--gray)', fontSize: 13, background: 'var(--bg-input)', color: 'var(--black)', resize: 'vertical', boxSizing: 'border-box' }}
          placeholder={t('panel_reply_placeholder')}
        />
      </div>

      {error && <p style={{ color: '#ef4444', fontSize: 12 }}>❌ {error}</p>}

      <div style={{ display: 'flex', gap: 8, marginTop: 'auto' }}>
        <button onClick={onClose} style={{ flex: 1, padding: '9px 0', borderRadius: 8, background: 'transparent', border: '1px solid var(--gray)', fontSize: 13, cursor: 'pointer', color: 'var(--black)' }}>{t('panel_close')}</button>
        <button onClick={save} disabled={saving} style={{ flex: 1, padding: '9px 0', borderRadius: 8, background: 'var(--blue)', color: 'white', border: 'none', fontSize: 13, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}>
          {saving ? '⏳...' : t('panel_save')}
        </button>
      </div>
    </div>
  );
};

const AdminRatings = () => {
  const [ratings, setRatings]         = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [loadingR, setLoadingR]       = useState(true);
  const [loadingS, setLoadingS]       = useState(true);
  const [errorR, setErrorR]           = useState('');
  const [errorS, setErrorS]           = useState('');
  const [avgScore, setAvgScore]       = useState(null);
  const [filterScore, setFilterScore] = useState(0);
  const [tab, setTab]                 = useState('ratings');
  const [panelSugg, setPanelSugg]     = useState(null);
  const [confirmState, setConfirmState] = useState(null); // { message, onConfirm }
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const SUGGESTION_CATEGORIES = getSuggestionCategories(t);
  const isAdmin = ['admin', 'dev'].includes(user?.role);
  const [offsetR, setOffsetR]     = useState(0);
  const [hasMoreR, setHasMoreR]   = useState(false);
  const [loadingMoreR, setLoadingMoreR] = useState(false);
  const PAGE_SIZE = 50;

  const loadRatings = async (off = 0, append = false) => {
    if (append) setLoadingMoreR(true);
    else { setLoadingR(true); setErrorR(''); }
    try {
      const data = await getAdminRatingsPaginated(off, PAGE_SIZE);
      setRatings(prev => append ? [...prev, ...(data.ratings || [])] : (data.ratings || []));
      setAvgScore(data.avg_score);
      setHasMoreR(data.has_more || false);
      setOffsetR(off + PAGE_SIZE);
    } catch (e) { setErrorR(e.message); }
    finally { setLoadingR(false); setLoadingMoreR(false); }
  };

  const loadSuggestions = async () => {
    setLoadingS(true); setErrorS('');
    try {
      const data = await getAdminSuggestions();
      setSuggestions(data.suggestions || []);
    } catch (e) { setErrorS(e.message); }
    finally { setLoadingS(false); }
  };

  useEffect(() => { loadRatings(); loadSuggestions(); }, []);

  const formatDate = (d) =>
    new Date(d).toLocaleString(lang === 'en' ? 'en-GB' : 'fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  const filtered = filterScore === 0 ? ratings : ratings.filter(r => r.score === filterScore);
  const scoreDistrib = STARS.map(s => ({ star: s, count: ratings.filter(r => r.score === s).length }));
  const maxDistrib   = Math.max(...scoreDistrib.map(s => s.count), 1);

  const handleDeleteRating = async (id) => {
    setConfirmState({
      message: t('admin_delete_confirm'),
      onConfirm: async () => {
        setConfirmState(null);
        try { await adminDeleteRating(id); setRatings(prev => prev.filter(r => r.id !== id)); }
        catch (e) { alert(t('err_prefix') + e.message); }
      }
    });
  };

  const handleDeleteSuggestion = async (id) => {
    setConfirmState({
      message: t('admin_delete_confirm'),
      onConfirm: async () => {
        setConfirmState(null);
        try { await adminDeleteSuggestion(id); setSuggestions(prev => prev.filter(s => s.id !== id)); }
        catch (e) { alert(t('err_prefix') + e.message); }
      }
    });
  };

  const handleSuggSaved = (id, status, reply) => {
    setSuggestions(prev => prev.map(s => s.id === id ? { ...s, status, admin_reply: reply } : s));
  };

  const suggLabels = { open: t('sugg_status_open'), in_progress: t('sugg_status_in_progress'), done: t('sugg_status_done'), rejected: t('sugg_status_rejected') };

  const renderDistrib = () => (
    <div style={{ background: 'var(--bg-surface)', borderRadius: 12, border: '1px solid var(--gray)', padding: '20px 24px', marginBottom: 20 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--black)', marginBottom: 14 }}>{t('distrib_title')}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {scoreDistrib.slice().reverse().map(function(item) {
          const star  = item.star;
          const count = item.count;
          return (
            <div key={star} onClick={() => setFilterScore(filterScore === star ? 0 : star)} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
              <span style={{ fontSize: 12, minWidth: 20, color: filterScore === star ? 'var(--blue)' : 'var(--text-muted)', fontWeight: filterScore === star ? 700 : 400 }}>{star}★</span>
              <div style={{ flex: 1, height: 14, background: 'var(--white)', borderRadius: 99, overflow: 'hidden', border: '1px solid var(--gray)' }}>
                <div style={{ height: '100%', width: `${Math.round((count / maxDistrib) * 100)}%`, background: filterScore === star ? 'var(--blue)' : '#f59e0b', borderRadius: 99, transition: 'width 0.4s' }} />
              </div>
              <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--black)', minWidth: 24, textAlign: 'right' }}>{count}</span>
            </div>
          );
        })}
      </div>
      {filterScore !== 0 && (
        <button onClick={() => setFilterScore(0)} style={{ marginTop: 12, fontSize: 12, color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
          {t('distrib_remove_filter')} ({filterScore} {t('distrib_stars')})
        </button>
      )}
    </div>
  );

  const renderRatings = () => {
    if (loadingR) return <div className="admin-table-wrapper" style={{ background: 'var(--bg-surface)', borderRadius: 10, border: '1px solid var(--gray)' }}>
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <tbody><SkeletonRows cols={10} rows={5} /></tbody>
    </table>
  </div>
    if (errorR)   return <div style={{ textAlign: 'center', padding: '48px 0', color: '#ef4444' }}>❌ {errorR}</div>;
    if (ratings.length === 0) return <div style={{ textAlign: 'center', padding: '64px 0', color: 'var(--text-muted)', fontSize: 14 }}>{t('no_ratings')}</div>;

    const shortId   = (id) => id ? id.substring(0, 8) + '...' : '';

    return (
      <div>
        {renderDistrib()}
        {filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--text-muted)', fontSize: 14 }}>{t('no_ratings_filter')}</div>
        ) : (
          <div className="admin-table-wrapper" style={{ background: 'var(--bg-surface)', borderRadius: 10, border: '1px solid var(--gray)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: 'var(--white)', borderBottom: '1px solid var(--gray)' }}>
                  <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: 'var(--black)', whiteSpace: 'nowrap' }}>{t('col_note')}</th>
                  <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: 'var(--black)', whiteSpace: 'nowrap' }}>{t('col_analysis')}</th>
                  <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: 'var(--black)', whiteSpace: 'nowrap' }}>{t('col_user')}</th>
                  <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: 'var(--black)', whiteSpace: 'nowrap' }}>{t('col_comment')}</th>
                  <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: 'var(--black)', whiteSpace: 'nowrap' }}>{t('col_date')}</th>
                  {isAdmin && <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: 'var(--black)', whiteSpace: 'nowrap' }}>{t('col_actions')}</th>}
                </tr>
              </thead>
              <tbody>
                {filtered.map(function(r, i) {
                  const rowBg = i % 2 === 0 ? 'transparent' : 'rgba(128,128,128,0.03)';
                  const name  = r.filename ? r.filename : shortId(r.analysis_id);
                  const email = r.user_email ? r.user_email : shortId(r.user_id);
                  return (
                    <tr key={r.id} style={{ borderBottom: '1px solid var(--gray)', background: rowBg }}>
                      <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}><StarDisplay score={r.score} /></td>
                      <td style={{ padding: '10px 14px', maxWidth: 180 }}>
                        <button type="button" onClick={() => telechargerRapport(r.analysis_id).catch(e => alert(e.message))}
                           style={{ color: 'var(--blue)', textDecoration: 'none', fontSize: 13, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                    background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', textAlign: 'left', maxWidth: '100%' }}>
                          {name}
                        </button>
                      </td>
                      <td style={{ padding: '10px 14px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{email}</td>
                      <td style={{ padding: '10px 14px', maxWidth: 300 }}>
                        {r.comment
                          ? <p style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: 'var(--black)' }}>{r.comment}</p>
                          : <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>-</span>}
                      </td>
                      <td style={{ padding: '10px 14px', color: 'var(--text-muted)', whiteSpace: 'nowrap', fontSize: 12 }}>{formatDate(r.created_at)}</td>
                      {isAdmin && (
                        <td style={{ padding: '10px 14px' }}>
                          <button onClick={() => handleDeleteRating(r.id)} style={{ padding: '4px 8px', borderRadius: 6, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', color: '#ef4444', fontSize: 11, cursor: 'pointer' }}>
                            {t('delete')}
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  const renderSuggestions = () => {
    if (loadingS) return <div style={{ textAlign: 'center', padding: '64px 0', color: 'var(--text-muted)' }}>⏳ Chargement...</div>;
    if (errorS)   return <div style={{ textAlign: 'center', padding: '48px 0', color: '#ef4444' }}>❌ {errorS}</div>;
    if (suggestions.length === 0) return <div style={{ textAlign: 'center', padding: '64px 0', color: 'var(--text-muted)', fontSize: 14 }}>{t('no_suggestions')}</div>;

    return (
      <div className="admin-table-wrapper" style={{ background: 'var(--bg-surface)', borderRadius: 12, border: '1px solid var(--gray)'}}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: 'var(--white)', borderBottom: '1px solid var(--gray)' }}>
              <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: 'var(--black)', whiteSpace: 'nowrap' }}>{t('col_category')}</th>
              <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: 'var(--black)' }}>{t('col_content')}</th>
              <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: 'var(--black)', whiteSpace: 'nowrap' }}>{t('col_status')}</th>
              <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: 'var(--black)' }}>{t('col_admin_reply')}</th>
              <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: 'var(--black)', whiteSpace: 'nowrap' }}>{t('col_user')}</th>
              <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: 'var(--black)', whiteSpace: 'nowrap' }}>{t('col_date')}</th>
              {isAdmin && <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: 'var(--black)', whiteSpace: 'nowrap' }}>{t('col_actions')}</th>}
            </tr>
          </thead>
          <tbody>
            {suggestions.map(function(s, i) {
              const sc  = SUGG_STATUS_COLORS[s.status] || SUGG_STATUS_COLORS.open;
              const rowBg = i % 2 === 0 ? 'transparent' : 'rgba(128,128,128,0.03)';
              return (
                <tr key={s.id} style={{ borderBottom: '1px solid var(--gray)', background: rowBg }}>
                  <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                    <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 99, background: '#ede9fe', color: '#5b21b6' }}>{SUGGESTION_CATEGORIES[s.category] || s.category}</span>
                  </td>
                  <td style={{ padding: '10px 14px', maxWidth: 220 }}>
                    <p style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: 'var(--black)', overflow: 'hidden', maxHeight: 60 }}>{s.content}</p>
                  </td>
                  <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                    <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 99, background: sc.bg, color: sc.text }}>{suggLabels[s.status] || s.status}</span>
                  </td>
                  <td style={{ padding: '10px 14px', maxWidth: 180 }}>
                    {s.admin_reply
                      ? <p style={{ margin: 0, fontSize: 12, color: 'var(--text-muted)', overflow: 'hidden', maxHeight: 40 }}>{s.admin_reply}</p>
                      : <span style={{ color: 'var(--text-muted)', fontStyle: 'italic', fontSize: 12 }}>-</span>}
                  </td>
                  <td style={{ padding: '10px 14px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{s.user_email || s.user_id?.substring(0, 8) + '...'}</td>
                  <td style={{ padding: '10px 14px', color: 'var(--text-muted)', whiteSpace: 'nowrap', fontSize: 12 }}>{formatDate(s.created_at)}</td>
                  {isAdmin && (
                    <td style={{ padding: '10px 14px' }}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button onClick={() => setPanelSugg(s)} style={{ padding: '4px 8px', borderRadius: 6, background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.3)', color: 'var(--blue)', fontSize: 11, cursor: 'pointer' }}>
                          {t('ann_status_label')}
                        </button>
                        <button onClick={() => handleDeleteSuggestion(s.id)} style={{ padding: '4px 8px', borderRadius: 6, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', color: '#ef4444', fontSize: 11, cursor: 'pointer' }}>
                          {t('delete')}
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', height: '100%' }}>
      {panelSugg && <SuggestionPanel sugg={panelSugg} onClose={() => setPanelSugg(null)} onSaved={handleSuggSaved} t={t} />}

      <div style={{ flex: 1, overflowY: 'auto', padding: '32px 24px 24px', minHeight: 0 }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>

          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
            <div>
              <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--black)', marginBottom: 4 }}>
                {tab === 'ratings' ? '⭐ ' + t('admin_ratings_title') : '💡 ' + t('admin_suggestions_title')}
              </h1>
              <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                {tab === 'ratings'
                  ? `${ratings.length} evaluation${ratings.length > 1 ? 's' : ''}${avgScore != null ? ' - moyenne ' + avgScore + '/5' : ''}`
                  : `${suggestions.length} suggestion${suggestions.length > 1 ? 's' : ''}`}
              </p>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {isAdmin && tab === 'ratings'     && <Button onClick={() => exportRatings().catch(e => alert(e.message))}     variant="outline" size="sm">{t('export_excel')}</Button>}
              {isAdmin && tab === 'suggestions' && <Button onClick={() => exportSuggestions().catch(e => alert(e.message))}  variant="outline" size="sm">{t('export_excel')}</Button>}
              <Button onClick={tab === 'ratings' ? loadRatings : loadSuggestions} variant="outline" size="sm">{t('btn_refresh')}</Button>
            </div>
          </div>

          {/* Onglets */}
          <div style={{ display: 'flex', gap: 0, marginBottom: 24, borderBottom: '1px solid var(--gray)' }}>
            {[['ratings', t('tab_ratings')], ['suggestions', t('tab_suggestions')]].map(function(item) {
              const key   = item[0];
              const label = item[1];
              return (
                <button
                  key={key}
                  onClick={() => setTab(key)}
                  style={{ padding: '10px 20px', background: 'none', border: 'none', borderBottom: tab === key ? '2px solid var(--blue)' : '2px solid transparent', color: tab === key ? 'var(--blue)' : 'var(--text-muted)', fontWeight: tab === key ? 600 : 400, fontSize: 14, cursor: 'pointer', transition: 'all 0.15s', marginBottom: -1 }}
                >
                  {label}
                </button>
              );
            })}
          </div>
          {confirmState && (
            <ConfirmModal
              message={confirmState.message}
              onConfirm={confirmState.onConfirm}
              onCancel={() => setConfirmState(null)}
            />
          )}
          {tab === 'ratings'     && renderRatings()}
          {tab === 'suggestions' && renderSuggestions()}
          {hasMoreR && (
            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <Button onClick={() => loadRatings(offsetR, true)} variant="outline" size="sm" disabled={loadingMoreR}>
                {loadingMoreR ? t('loading_more') : t('load_more')}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminRatings;